import { ChangeEvent, useEffect, useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { FaUser } from "react-icons/fa6";
import editIcon from "@/assets/icons/edit.svg";
import {
  UserDetails,
  getUserDetails,
  updateUserDetails,
} from "../services/userProfileServices";
import { useLoading } from "@/contexts/LoadingContext";
import toastService from "@/services/toastService";
import { useDispatch, useSelector } from "react-redux";
import { setUser } from "@/features/auth/UserSlice";
import { User } from "@/features/auth/types";
import { AppDispatch, RootState } from "@/store";

interface UserImage {
  id: string;
  file: File;
  preview: string;
}

// Type definition for form data
interface FormData {
  fullName: string;
  phoneNumber: string;
  addressDetails: string;
}

export default function UserProfile() {
    const currentUser = useSelector(
    (root: RootState) => root.rootReducer.userReducer.user
  );
  const dispatch = useDispatch<AppDispatch>();
  const [isUpdated, setIsUpdated] = useState(false);
  const [userDetails, setUserDetails] = useState<UserDetails | undefined>(
    undefined
  );
  const [formData, setFormData] = useState<FormData>({
    fullName: "",
    phoneNumber: "",
    addressDetails: "",
  });
  const [userImage, setUserImage] = useState<UserImage | undefined>(undefined);

  const { setLoading } = useLoading();

  useEffect(() => {
    // Fetch user details from API
    const fetchUserDetails = async () => {
      setLoading(true);
      const response = await getUserDetails();
      if (response && response.statusCode === 200) {
        console.log("User Details:", response);
        setUserDetails(response.data.userDetails);
        setFormData({
          fullName: response.data.userDetails.fullName,
          phoneNumber: response.data.userDetails.phoneNumber,
          addressDetails: response.data.userDetails.addressDetails || "",
        });
           if (response.data.userDetails.profileImage) {
        setUserImage({
          id: "existing_profile_image",
          file: new File([], ""), // placeholder empty File
          preview: response.data.userDetails.profileImage, // URL from backend
        });
      }
      }
      setLoading(false);
    };
    fetchUserDetails();
  }, [isUpdated]);

  // Cleanup object URL on component unmount
  useEffect(() => {
    return () => {
      if (userImage?.preview) {
        URL.revokeObjectURL(userImage.preview);
      }
    };
  }, [userImage]);

  /* Handles input changes and updates form state */
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  /* Handles user image upload */
  const handleImageUpload = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Create a unique ID for the image
      const imageId = `img_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

      // Create URL for preview
      const previewUrl = URL.createObjectURL(file);

      // Revoke the previous object URL if it exists
      if (userImage?.preview) {
        URL.revokeObjectURL(userImage.preview);
      }

      setUserImage({
        id: imageId,
        file: file,
        preview: previewUrl,
      });
    }
  };

  /* Handles form submission */
const handleSubmit = async (e: React.FormEvent) => {
  e.preventDefault();
  try {
    setLoading(true);

    const form = new FormData();

    if (typeof formData.fullName === "string" && formData.fullName.trim()) {
      form.append("fullName", formData.fullName.trim());
    }

    if (typeof formData.phoneNumber === "string" && formData.phoneNumber.trim()) {
      form.append("phoneNumber", formData.phoneNumber.trim());
    }

    if (typeof formData.addressDetails === "string" && formData.addressDetails.trim()) {
      form.append("addressDetails", formData.addressDetails.trim());
    }

    if (userImage?.file) {
      form.append("profileImage", userImage.file);
    }

    // Debugging log
    for (const [key, val] of form.entries()) {
      console.log(`${key}:`, val);
    }

    const response = await updateUserDetails(form);
    if (response && response.statusCode === 200) {
     setIsUpdated(!isUpdated);
    toastService.success("Details saved successfully");

    console.log("Raw response", response);
    const data = response.data;

    console.log("Parsed data:", data);

    const updatedFields = {
      ...(data?.addressDetails && { address: data.addressDetails }),
      ...(data?.phoneNumber && { phoneNumber: data.phoneNumber }),
      ...(data?.fullName && { fullName: data.fullName }),
      ...(data?.profileImage && { avatar_url: data.profileImage }),
    };

    const updatedUser: User = {
      ...currentUser,
      ...updatedFields,
    };

    console.log("Updated User1:", updatedUser);

    dispatch(setUser(updatedUser));

    console.log("LocalStorage user before update:", localStorage.getItem("user"));
    localStorage.setItem("user", JSON.stringify(updatedUser));
    console.log("LocalStorage user after update:", localStorage.getItem("user"));
    }
  } catch (error) {
    console.log("Error while updating user details", error);
  } finally {
    setLoading(false);
  }
};



  const labelStyle = "block text-sm font-medium text-[#616161] mb-2";
  const inputStyle = "w-full rounded-lg";
  const fieldStyle =
    "bg-white rounded-lg border border-slate-200 py-[13px] px-3 text-sm font-normal cursor-not-allowed";

  return (
    <div className="mx-auto p-7">
      {/* Header */}
      <h1 className="text-xl font-medium text-bmpGreen-500 mb-6">
        Personal Information
      </h1>

      {/* Form Card */}
      <Card className="bg-[#f6f8fa] p-7 border-none rounded-2xl">
        <div>
          {/* Avatar Section */}
          <div className="mb-8 flex items-center">
            <div className="relative">
              {userImage ? (
                <div
                  key={userImage.id}
                  className="w-28 h-28 rounded-full overflow-hidden border border-[#dadada]"
                >
                  <img
                    src={userImage.preview}
                    alt="user image"
                    className="w-28 h-28 object-cover"
                  />
                </div>
              ) : (
                <Avatar className="h-28 w-28 bg-gray-200">
                  <AvatarFallback className="bg-gray-200 text-gray-600">
                    <FaUser size={50} />
                  </AvatarFallback>
                </Avatar>
              )}
              <label
                htmlFor="image-upload"
                className="absolute bottom-0 right-0 rounded-full cursor-pointer"
              >
                <img src={editIcon} alt="edit icon" />
                <input
                  id="image-upload"
                  type="file"
                  accept="image/png, image/jpeg, image/jpg, image/gif, image/webp"
                  className="hidden"
                  onChange={handleImageUpload}
                />
              </label>
            </div>
          </div>

          {/* Form Fields */}
          <div className="grid lg:grid-cols-2 xl:grid-cols-3 gap-6 mb-12">
            {/* Full Name */}
            <div>
              <label htmlFor="fullName" className={labelStyle}>
                Full Name
              </label>
              <Input
                id="fullName"
                name="fullName"
                placeholder="Enter Full Name"
                value={formData.fullName}
                onChange={handleInputChange}
                className={inputStyle}
              />
            </div>

            {/* Email */}
            <div>
              <label htmlFor="email" className={labelStyle}>
                Email
              </label>
              <div className={fieldStyle} aria-disabled="true" >{userDetails?.email}</div>
            </div>

            {/* Phone Number */}
            <div>
              <label htmlFor="phoneNumber" className={labelStyle}>
                Phone Number
              </label>
              <Input
                id="phoneNumber"
                name="phoneNumber"
                placeholder="Enter Phone Number"
                value={formData.phoneNumber}
                onChange={handleInputChange}
                className={inputStyle}
              />
            </div>

            {/* Company Name */}
            <div>
              <label htmlFor="companyName" className={labelStyle}>
                Company Name
              </label>
              <div className={fieldStyle} >{userDetails?.companyName || "-"}</div>
            </div>

            {/* Country */}
            <div>
              <label htmlFor="country" className={labelStyle}>
                Country
              </label>
              <div className={fieldStyle}>{userDetails?.countryName || "-"}</div>
            </div>

            {/* Address */}
            <div>
              <label htmlFor="addressDetails" className={labelStyle}>
                Address
              </label>
              <Input
                id="addressDetails"
                name="addressDetails"
                placeholder="Enter Address"
                value={formData.addressDetails}
                onChange={handleInputChange}
                className={inputStyle}
              />
            </div>
          </div>

          {/* Submit Button */}
          <div>
            <Button
              onClick={handleSubmit}
              className="bg-bmpGreen-500 hover:bg-bmpGreen-600 text-white px-12 py-3 rounded-lg text-lg font-semibold"
            >
              Save Details
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}
